/**
 * Single source of truth for page-level JSON-LD.
 *
 * Both the React pages (via PageSEO) and scripts/prerender.ts build their
 * schema arrays from these functions, so the static HTML a non-rendering
 * crawler sees carries exactly the same nodes — same content, same order — as
 * the hydrated DOM.
 *
 * Sitewide Organization + LocalBusiness are NOT included here: GlobalSchema
 * owns those and the prerenderer prepends them with data-seo-global="1".
 */
import { COMPANY, breadcrumbSchema, faqSchema, serviceSchema } from "../lib/seo";
import { homepageFaqs, homepageServiceSchemaInputs } from "./homepage-content";
import { electricalFaqs, hvacFaqs, energyFaqs, stadiumFaqs } from "./hub-faqs";
import { getLocationEnrichment } from "./location-enrichment";
import type { LocationData } from "./locations";

/* ── Homepage ── */
export function homepageSchemas(): object[] {
  return [
    breadcrumbSchema([{ name: "Home", url: "/" }]),
    faqSchema(homepageFaqs.map(f => ({ question: f.q, answer: f.a }))),
    ...homepageServiceSchemaInputs.map(s => serviceSchema(s)),
  ].filter(Boolean) as object[];
}

/* ── Hub overviews ── */
export const hubSchemas: Record<string, () => object[]> = {
  "/electrical-services": () => [
    faqSchema(electricalFaqs),
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Electrical Services", url: "/electrical-services" }]),
    serviceSchema({
      name: "Electrical Services Louisville KY",
      description: "Licensed commercial & industrial electrical contractor in Louisville, KY. Same-day service, panel upgrades & emergency repair.",
      url: "/electrical-services",
      category: "Electrical Contractor",
    }),
  ].filter(Boolean) as object[],

  "/hvac-services": () => [
    faqSchema(hvacFaqs),
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "HVAC Services", url: "/hvac-services" }]),
    serviceSchema({
      name: "Heating & Cooling Services Louisville KY",
      description: "24/7 heating & cooling services — AC repair, furnace maintenance, heat pump installation & commercial HVAC in Louisville, KY.",
      url: "/hvac-services",
      category: "Heating and Cooling Service",
    }),
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "24/7 HVAC Services",
      description: "24/7 emergency HVAC service for commercial, industrial, and residential properties in Louisville KY.",
      provider: { "@id": "https://hadleyenergysolutions.com/#organization" },
      areaServed: { "@type": "City", name: "Louisville", containedInPlace: { "@type": "State", name: "Kentucky" } },
      serviceType: "HVAC Service",
      availableChannel: {
        "@type": "ServiceChannel",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-502-921-3777",
          contactType: "emergency",
          availableLanguage: "English",
          hoursAvailable: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "00:00",
            closes: "23:59",
          },
        },
      },
      offers: { "@type": "Offer", priceCurrency: "USD", availability: "https://schema.org/InStock", description: "Free estimates available" },
    },
  ].filter(Boolean) as object[],

  "/energy-management": () => [
    faqSchema(energyFaqs),
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Energy Management", url: "/energy-management" }]),
    serviceSchema({
      name: "Energy Management Services Louisville KY",
      description: "LED retrofits, energy audits & guaranteed savings programs for Louisville, KY commercial properties.",
      url: "/energy-management",
      category: "Energy Management",
    }),
  ].filter(Boolean) as object[],

  "/industries": () => [
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Industries", url: "/industries" }]),
    serviceSchema({
      name: "Industrial Electrical & HVAC Contractor",
      description: "Industrial electrical and HVAC contractor in Louisville, KY for manufacturing plants, warehouses, hospitals, schools, stadiums, hotels, data centers, and municipal facilities.",
      url: "/industries",
      category: "Industrial Contracting",
    }),
  ].filter(Boolean) as object[],

  "/stadium-sports-lighting": () => [
    faqSchema(stadiumFaqs),
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Stadium & Sports Lighting", url: "/stadium-sports-lighting" }]),
    serviceSchema({
      name: "Stadium Lighting Installation Louisville KY",
      description: "Professional stadium lighting installation, LED sports field lighting, and arena lighting services throughout Kentucky and Southern Indiana.",
      url: "/stadium-sports-lighting",
      category: "Stadium Lighting Contractor",
    }),
  ].filter(Boolean) as object[],

  "/service-area": () => [
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Service Areas", url: "/service-area" }]),
    serviceSchema({
      name: "Electrical & HVAC Service Across KY & Southern Indiana",
      description:
        "Licensed electrical, HVAC, and energy services across Louisville, Lexington, Shepherdsville, Mt. Washington, Jeffersontown, Elizabethtown, La Grange, and Southern Indiana cities including New Albany, Jeffersonville, and Clarksville.",
      url: "/service-area",
      category: "Service Area",
    }),
  ].filter(Boolean) as object[],
};

/* ── City pages ── */

/** Top service links rendered on every city page (rail + "Our Services in …"). */
export const locationTopServices = [
  { label: "Commercial Electrical Contractor", href: "/electrical-services/commercial-electrical-contractor" },
  { label: "Industrial Electrical Contractor", href: "/electrical-services/industrial-electrical-contractor" },
  { label: "Electrical Service & Repair", href: "/electrical-services/electrical-service-and-repair" },
  { label: "24 Hour Emergency Electrician", href: "/electrical-services/emergency-electrical-service" },
  { label: "Transformer Installation", href: "/electrical-services/transformer-installation" },
  { label: "Fire Alarm Installation", href: "/electrical-services/fire-alarm-systems" },
  { label: "Commercial HVAC", href: "/hvac-services/commercial-hvac" },
  { label: "Lighting Retrofits", href: "/energy-management/lighting-retrofits" },
  { label: "Energy Audits", href: "/energy-management/energy-audits" },
  { label: "Stadium Lighting", href: "/stadium-sports-lighting" },
];

const googleMapsUrl = (city: string, state: string) =>
  `https://www.google.com/maps/search/Hadley+Energy+Solutions+${encodeURIComponent(city)}+${encodeURIComponent(state)}`;

export function locationFaqs(location: LocationData) {
  const enrichment = getLocationEnrichment(location.slug);
  const standardFaqs = [
    { question: `How much does a commercial electrician cost in ${location.city}?`, answer: `Commercial electrician rates in ${location.city} typically range from $75 to $150 per hour. Hadley Energy Solutions offers free estimates. Call (502) 921-3777.` },
    { question: `Do you offer same-day service in ${location.city}?`, answer: `Yes. We provide same-day emergency electrical service to ${location.city}, ${location.state} and surrounding areas.` },
    { question: `What electrical services do you offer in ${location.city}?`, answer: `We provide commercial and industrial electrical contracting, HVAC installation and repair, energy management, lighting design and retrofits, generator installation, and more in ${location.city}.` },
    { question: `Are you licensed to work in ${location.city}, ${location.state}?`, answer: `Yes. Hadley Energy Solutions holds Kentucky Contractor License CE64623 and Master Electrician License ME64500. We're fully licensed and insured.` },
  ];
  return [...enrichment.uniqueFaqs, ...standardFaqs];
}

export function locationSchemas(location: LocationData): object[] {
  return [
    breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Service Areas", url: "/service-area" }, { name: `${location.city}, ${location.state}`, url: `/service-area/${location.slug}` }]),
    faqSchema(locationFaqs(location)),
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: `Electrical & HVAC Contractor in ${location.city}`,
      name: `Electrical & HVAC Contractor in ${location.city}`,
      description: location.metaDescription,
      url: `${COMPANY.url}/service-area/${location.slug}`,
      provider: {
        "@type": "Electrician",
        "@id": `${COMPANY.url}/#localbusiness`,
        name: COMPANY.name,
        url: COMPANY.url,
        telephone: COMPANY.phone,
        image: `${COMPANY.url}/images/og-image.jpg`,
        address: {
          "@type": "PostalAddress",
          streetAddress: "965 Old Preston Hwy N",
          addressLocality: "Louisville",
          addressRegion: "KY",
          postalCode: "40229",
          addressCountry: "US",
        },
        geo: { "@type": "GeoCoordinates", latitude: 38.0689, longitude: -85.6719 },
        priceRange: "$$",
        hasMap: googleMapsUrl(location.city, location.state),
      },
      areaServed: { "@type": "City", name: `${location.city}, ${location.state}` },
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        description: "Free estimates available",
      },
    },
  ].filter(Boolean) as object[];
}
